import type { VariableEntity } from '../../../../../../services/entities/VariablesEntity';
import { formatVariableValueWithPricing } from '../../../../helpers/formatVariableValueWithPricing';
import type { VariableNode } from 'services/entities/VariablesEntity';
import { hasFailedValidation } from 'services/entities/variableValidation';
import type { FlattenedOption } from 'components/editor/variables/types';
import { ContentType } from 'utils/getContentType';

export const formatVariable = (name: string | number, variable?: VariableEntity | FlattenedOption | VariableNode, currency?: string) => {
  if (!variable) {
    return name;
  }

  const formattedValue = formatVariableValueWithPricing(variable, currency);

  return (formattedValue || variable.value || '').toString().trim();
};

export const isWarning = (variableNode: VariableNode | undefined, contentType: ContentType): boolean => {
  if (!variableNode) return true;
  if (contentType === ContentType.TEMPLATE || contentType === ContentType.SECTION) return false;
  const { value } = variableNode;
  return value === null || value === undefined || value === '';
};

export type VariableFlagState = 'failed-validation' | 'not-assigned' | null;

// Failed-validation wins over not-assigned, so an UNPARSEABLE stub (value: null) isn't
// misreported as a plain missing value.
export const getVariableFlagState = (variableNode: VariableNode | undefined, contentType: ContentType): VariableFlagState => {
  if (variableNode && hasFailedValidation(variableNode)) return 'failed-validation';
  if (isWarning(variableNode, contentType)) return 'not-assigned';
  return null;
};

export const getDisplayVariable = (data: VariableNode, contentType: ContentType, currencySymbol?: string) => {
  const { path, value, friendlyName, name } = data;
  const hasValue = value !== null && value !== undefined && value !== '';
  const adjustedName = `[${friendlyName.replace(/\s+/g, '_')}]`;
  const displayValue = hasValue && contentType === ContentType.DOCUMENT ? formatVariable(adjustedName, data, currencySymbol) : friendlyName;
  const displayedTitle = `${friendlyName} · ${adjustedName}`;
  const tooltipTitle = `[${path}]`;
  return { displayValue, tooltipTitle, displayedTitle, name, adjustedName, friendlyName };
};

// Matches template+objectId before name+objectId, so a chip never resolves to the wrong array instance when siblings share a name (DT-1886).
// A pattern chip (no objectId) keeps name-only lookup.
export function findVariableForChip<T extends { name: string; objectId?: string | null; template?: string | null }>(
  variables: T[] | undefined,
  attrs: { name: string; objectId?: string | null; template?: string | null }
): T | undefined {
  if (!variables) return undefined;

  if (attrs.objectId && attrs.template) {
    const byTemplate = variables.find((variable) => variable?.template === attrs.template && variable.objectId === attrs.objectId);
    if (byTemplate) return byTemplate;
  }

  if (attrs.objectId) {
    return variables.find((variable) => variable?.name === attrs.name && variable.objectId === attrs.objectId);
  }

  return variables.find((variable) => variable?.name === attrs.name);
}
